import { Injectable } from '@angular/core';
import { IScheduleDay } from '@model/shared/schedule-day.model';
import { ISchedule } from '@model/shared/schedule.model';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'; // import plugin
import 'dayjs/locale/pt'; // import locale
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';

dayjs.extend(isSameOrBefore); // use plugin
dayjs.locale('pt'); // use loca

@Injectable({
  providedIn: 'root',
})
export class SchedulerService {
  static BsDatepickerConfig: Partial<BsDatepickerConfig> = {
    customTodayClass: 'date-active',
    containerClass: 'theme-default',
    showWeekNumbers: false,
    isAnimated: true,
    // useUtc          : true,
    rangeInputFormat: 'DD/MM/YYYY',
    dateInputFormat: 'DD/MM/YYYY',
  };

  getTotalDays(schedule: ISchedule): number {
    let total = 0;
    if (schedule && schedule.days && schedule.days.length > 0) {
      for (let i = 0; i < schedule.days.length; i++) {
        const day = schedule.days[i];
        total += this.getTotalDaysPerPeriod(day);
      }
    }
    total = Math.round(total * 100) / 100;
    return total;
  }

  getTotalHours(schedule: ISchedule): number {
    let total = 0;
    if (schedule && schedule.days && schedule.days.length > 0) {
      for (let i = 0; i < schedule.days.length; i++) {
        total += this.getTotalHoursPerPeriod(schedule.days[i]);
      }
    }
    return total;
  }

  getTotalDaysPerPeriod(day: IScheduleDay): number {
    if (day && day.dateStart && day.dateEnd) {
      let start = dayjs(day.dateStart).startOf('day').hour(6);
      const end = dayjs(day.dateEnd).endOf('day').hour(6);

      let days = 0;

      while (start.isSameOrBefore(end, 'day')) {
        if (day.dow && day.dow.includes(start.day())) {
          days++;
        }
        start = start.add(1, 'day');
      }
      if (!day.holidays) {
        day.holidays = 0;
      }
      day.daysInPeriod = days - day.holidays;
      return days - day.holidays;
    }
    return 0;
  }

  getTotalHoursPerPeriod(day: IScheduleDay): number {
    return this.getTotalDaysPerPeriod(day) * this.getHoursPerDay(day);
  }

  getHoursPerDay(day: IScheduleDay): number {
    let morningDifferenceMinutes: number;
    let afternoonDifferenceMinutes: number;
    let nightDifferenceMinutes: number;
    if (day.morning) {
      morningDifferenceMinutes = 300;
    } else {
      const morningFrom = dayjs().startOf('day').hour(dayjs(day.morningFrom).hour()).minute(dayjs(day.morningFrom).minute());
      const morningTo = dayjs().startOf('day').hour(dayjs(day.morningTo).hour()).minute(dayjs(day.morningTo).minute());
      morningDifferenceMinutes = morningTo.diff(morningFrom, 'minutes');
      if (!morningDifferenceMinutes) {
        morningDifferenceMinutes = 0;
      }
    }
    if (day.afternoon) {
      afternoonDifferenceMinutes = 300;
    } else {
      const afternoonFrom = dayjs().startOf('day').hour(dayjs(day.afternoonFrom).hour()).minute(dayjs(day.afternoonFrom).minute());
      const afternoonTo = dayjs().startOf('day').hour(dayjs(day.afternoonTo).hour()).minute(dayjs(day.afternoonTo).minute());
      afternoonDifferenceMinutes = afternoonTo.diff(afternoonFrom, 'minutes');
      if (!afternoonDifferenceMinutes) {
        afternoonDifferenceMinutes = 0;
      }
    }
    if (day.night) {
      nightDifferenceMinutes = 300;
    } else {
      const nightFrom = dayjs().startOf('day').hour(dayjs(day.nightFrom).hour()).minute(dayjs(day.nightFrom).minute());
      const nightTo = dayjs().startOf('day').hour(dayjs(day.nightTo).hour()).minute(dayjs(day.nightTo).minute());
      nightDifferenceMinutes = nightTo.diff(nightFrom, 'minutes');
      if (!nightDifferenceMinutes) {
        nightDifferenceMinutes = 0;
      }
    }
    day.hoursPerDay = (morningDifferenceMinutes + afternoonDifferenceMinutes + nightDifferenceMinutes) / 60;
    const total = (morningDifferenceMinutes + afternoonDifferenceMinutes + nightDifferenceMinutes) / 60;
    return Math.round(total * 100) / 100;
  }

  getStartDate(schedule: ISchedule): dayjs.Dayjs | undefined {
    if (schedule) {
      const days = schedule.days;

      if (!days || days.length === 0) {
        return schedule.startDate;
      }
      days.sort(function (a: IScheduleDay, b: IScheduleDay): number {
        if (!a.dateStart) {
          return 1;
        }
        if (!b.dateStart) {
          return 0;
        }
        if (a.dateStart.isBefore(b.dateStart, 'day')) {
          return -1;
        }
        if (a.dateStart.isSame(b.dateStart, 'day')) {
          return 0;
        }
        return 1;
      });
      return days[0].dateStart;
    }
    return undefined;
  }

  getEndDate(schedule: ISchedule): dayjs.Dayjs | undefined {
    if (schedule) {
      const days = schedule.days;
      if (!days || days.length === 0) {
        return schedule.endDate;
      }
      days.sort(function (a: IScheduleDay, b: IScheduleDay): number {
        if (!a.dateEnd) {
          return 1;
        }
        if (!b.dateEnd) {
          return 0;
        }
        if (a.dateEnd.isBefore(b.dateEnd, 'day')) {
          return 1;
        }
        if (a.dateEnd.isSame(b.dateEnd, 'day')) {
          return 0;
        }
        return -1;
      });
      return days[0].dateEnd;
    }
    return undefined;
  }
}
